import { useId, useState } from 'react';
import { submitContact } from '@/api/submitContact';
import { Eyebrow } from '@/components/content/Eyebrow';
import { Button } from '@/components/interactive/Button';
import { RLink } from '@/components/interactive/RLink';
import { Reveal } from '@/components/utils/RevealOnScroll';
import { SEO } from '@/components/utils/SEO';
import { AtomicMark } from '@/components/visuals/AtomicMark';
import { type ContactInput, contactSchema } from '@/lib/contactSchema';

interface FieldProps {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  error?: string;
  hint?: string;
  textarea?: boolean;
  optional?: boolean;
}

function Field({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  error,
  hint,
  textarea,
  optional,
}: FieldProps) {
  const [focus, setFocus] = useState(false);
  const baseId = useId();
  const inputId = `${baseId}-${name}`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy =
    [errorId, hint && !error ? hintId : undefined].filter(Boolean).join(' ') ||
    undefined;
  const required = !optional;
  const base: React.CSSProperties = {
    width: '100%',
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    backdropFilter: 'blur(12px) saturate(140%)',
    WebkitBackdropFilter: 'blur(12px) saturate(140%)',
    border: `1px solid ${error ? 'var(--error)' : focus ? 'var(--accent)' : 'var(--border-default)'}`,
    borderRadius: 6,
    padding: '12px 14px',
    color: 'var(--ink-primary)',
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    transition: 'border-color 160ms var(--ease-out)',
    resize: textarea ? 'vertical' : 'none',
    minHeight: textarea ? 100 : undefined,
    boxShadow: focus ? '0 0 0 3px var(--radium-glow)' : 'none',
  };
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8,
          alignItems: 'baseline',
        }}
      >
        <label
          htmlFor={inputId}
          style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-primary)' }}
        >
          {label}
          {required && (
            <span aria-hidden="true" style={{ color: 'var(--accent)', marginLeft: 4 }}>
              *
            </span>
          )}
        </label>
        {optional && (
          <span
            className="text-mono"
            style={{ fontSize: 11, color: 'var(--ink-muted)' }}
          >
            optional
          </span>
        )}
      </div>
      {hint && !error && (
        <div
          id={hintId}
          className="text-mono"
          style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 8 }}
        >
          {hint}
        </div>
      )}
      {textarea ? (
        <textarea
          id={inputId}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={base}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-required={required}
          required={required}
        />
      ) : (
        <input
          id={inputId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={base}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          aria-required={required}
          required={required}
        />
      )}
      {error && (
        <div
          id={errorId}
          role="alert"
          style={{ marginTop: 6, fontSize: 12, color: 'var(--error)' }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

// Server-side error keys (from the Lambda) → user-facing messages.
const SERVER_FIELD_MESSAGES: Record<string, string> = {
  required: 'Required.',
  invalid: "Doesn't look like an email.",
  too_long: 'Too long.',
};

export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [values, setValues] = useState<ContactInput>({
    name: '',
    email: '',
    company: '',
    role: '',
    stack: '',
    surfaces: '',
    message: '',
  });

  const update =
    (k: keyof ContactInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const parsed = contactSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    const result = await submitContact(parsed.data);
    setLoading(false);

    if (result.ok) {
      setSubmitted(true);
      return;
    }

    if (result.kind === 'validation') {
      const mapped: Record<string, string> = {};
      for (const [k, code] of Object.entries(result.fields)) {
        mapped[k] = SERVER_FIELD_MESSAGES[code] ?? 'Invalid.';
      }
      setErrors(mapped);
      setSubmitError('Please fix the highlighted fields and try again.');
      return;
    }

    setSubmitError(result.message);
  };

  return (
    <>
      <SEO
        title="Contact — Radium"
        description="Tell us about your stack. A real person reads every message and we reply within two business days."
        canonical="https://radium.love/contact"
        ogImage="/og/og-contact.png"
      />
      <section className="page-hero" style={{ paddingBottom: 64 }}>
        <div className="container">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="text-h1" style={{ marginTop: 24, maxWidth: 900 }}>
              Tell us about{' '}
              <span style={{ color: 'var(--accent)' }}>your stack</span>.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p
              className="text-body-lg"
              style={{ marginTop: 28, maxWidth: 660 }}
            >
              A real person reads every message. We reply within two business
              days. If we're not the right fit, we'll say so directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr',
              gap: 64,
            }}
            className="contact-grid"
          >
            <Reveal>
              <div
                className="glass glass--settled"
                style={{ padding: 36, borderRadius: 12 }}
              >
                {submitted ? (
                  <div style={{ padding: '32px 0', textAlign: 'center' }}>
                    <AtomicMark size={36} tone="var(--accent)" />
                    <h2 className="text-h3" style={{ marginTop: 24 }}>
                      Got it.
                    </h2>
                    <p style={{ marginTop: 12, color: 'var(--ink-secondary)' }}>
                      We'll be in touch within two business days.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} noValidate>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 20,
                      }}
                      className="contact-form-grid"
                    >
                      <Field
                        label="Name"
                        name="name"
                        value={values.name}
                        onChange={update('name')}
                        error={errors.name}
                      />
                      <Field
                        label="Work email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={update('email')}
                        error={errors.email}
                      />
                      <Field
                        label="Company"
                        name="company"
                        value={values.company}
                        onChange={update('company')}
                        error={errors.company}
                      />
                      <Field
                        label="Role"
                        name="role"
                        placeholder="VP Product, Head of Content, Director of Data"
                        value={values.role}
                        onChange={update('role')}
                        error={errors.role}
                      />
                    </div>
                    <div style={{ marginTop: 20, display: 'grid', gap: 20 }}>
                      <Field
                        label="Current data stack"
                        name="stack"
                        textarea
                        placeholder="Warehouse, CDP, CMS — whatever you can share"
                        value={values.stack}
                        onChange={update('stack')}
                        error={errors.stack}
                      />
                      <Field
                        label="Surfaces you want to personalize"
                        name="surfaces"
                        textarea
                        placeholder="App feeds, web, modules — whatever's on the roadmap"
                        value={values.surfaces}
                        onChange={update('surfaces')}
                        error={errors.surfaces}
                      />
                      <Field
                        label="Anything else?"
                        name="message"
                        textarea
                        optional
                        value={values.message}
                        onChange={update('message')}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: 28,
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        loading={loading}
                        chevron={!loading}
                      >
                        {loading ? 'Sending…' : 'Send'}
                      </Button>
                      <span
                        className="text-mono"
                        style={{ color: 'var(--ink-muted)', fontSize: 12 }}
                      >
                        Two business days. No SDR funnel.
                      </span>
                    </div>
                    {submitError && (
                      <div
                        role="alert"
                        aria-live="polite"
                        style={{
                          marginTop: 20,
                          padding: '12px 14px',
                          background: 'rgba(200, 64, 42, 0.08)',
                          border: '1px solid var(--error)',
                          borderRadius: 6,
                          color: 'var(--error)',
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {submitError}
                      </div>
                    )}
                  </form>
                )}
              </div>
            </Reveal>

            <div style={{ position: 'sticky', top: 100, alignSelf: 'start' }}>
              <Reveal delay={120}>
                <div
                  className="glass glass--settled"
                  style={{ padding: 32, borderRadius: 12 }}
                >
                  <div className="text-eyebrow" style={{ marginBottom: 16 }}>
                    Or skip the form
                  </div>
                  <h2 className="text-h3" style={{ marginBottom: 16 }}>
                    Email us directly.
                  </h2>
                  <p
                    style={{
                      color: 'var(--ink-secondary)',
                      fontSize: 15,
                      lineHeight: 1.7,
                      marginBottom: 28,
                    }}
                  >
                    Reach out at{' '}
                    <span
                      style={{
                        color: 'var(--accent)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      hello@radium.love
                    </span>
                    .
                  </p>
                  <p
                    style={{
                      color: 'var(--ink-secondary)',
                      fontSize: 15,
                      lineHeight: 1.7,
                      marginBottom: 28,
                    }}
                  >
                    Curious about the studio behind Radium?{' '}
                    <RLink
                      href="https://unicorn.love"
                      chevron
                      target="_blank"
                      rel="noreferrer"
                    >
                      Visit Unicorn
                    </RLink>
                    .
                  </p>
                  <div
                    className="glass-panel-inset"
                    style={{ padding: 20, borderRadius: 10 }}
                  >
                    <div className="text-eyebrow" style={{ marginBottom: 12 }}>
                      What happens next
                    </div>
                    <ol
                      style={{
                        listStyle: 'none',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 13,
                        color: 'var(--ink-secondary)',
                      }}
                    >
                      <li>
                        <span style={{ color: 'var(--accent)' }}>01</span>{' '}
                        &nbsp;A real person reads it.
                      </li>
                      <li>
                        <span style={{ color: 'var(--accent)' }}>02</span>{' '}
                        &nbsp;We reply within 2 business days.
                      </li>
                      <li>
                        <span style={{ color: 'var(--accent)' }}>03</span>{' '}
                        &nbsp;If we're not a fit, we tell you.
                      </li>
                    </ol>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 880px) { .contact-grid { grid-template-columns: 1fr !important; gap: 32px !important; } }
          @media (max-width: 600px) { .contact-form-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>
    </>
  );
}
